import { type ReactNode } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { Navigate, Route, Routes } from "react-router-dom";
import {
	isHourglassInternal,
	isHourglassInternalStrict,
} from "@/shared/config/hourglassPermissions";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import InternalSidebar from "./components/InternalSidebar";
import EntriesView from "./views/EntriesView";
import SandboxView from "./views/SandboxView";
import MetricsView from "./views/MetricsView";
import UpdatesView from "@/shared/components/Updates/UpdatesView";
import { SidebarProvider, SidebarInset } from "@/shared/components/ui/sidebar";
import { ColumnPickerProvider } from "@/features/attorney/components/HeaderBar/ColumnPickerContext";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";

function Titled({ title, children }: { title: string; children: ReactNode }) {
	useDocumentTitle(title);
	return <>{children}</>;
}

/**
 * Internal Dashboard Page
 *
 * Entry point for Hourglass internal staff (anyone with an `@hourglass.law`
 * email). Lets them review attorney time entries on behalf of a firm using
 * the tenant subdomain they're currently on.
 *
 * Permissions are read inline; sub-views are mounted via nested routes.
 */
export default function InternalPage() {
	const { user } = useAuth();
	const allowed = isHourglassInternal(user?.email);

	if (!allowed) return <Navigate to="/entries" replace />;

	return (
		<SidebarProvider>
			<InternalSidebar />
			<SidebarInset className="bg-background overflow-auto">
				<ColumnPickerProvider>
					<ErrorBoundary>
						<Routes>
							<Route index element={<Navigate to="entries" replace />} />
							<Route path="entries" element={<Titled title="Internal - Entries"><EntriesView /></Titled>} />
							<Route path="calendar" element={<Titled title="Internal - Entries"><EntriesView /></Titled>} />
							<Route
								path="metrics"
								element={
									// Stricter than the page gate above: metrics ranks every
									// attorney at the firm, so it opts out of the DEV override.
									// Gated HERE rather than inside MetricsView so its data
									// hooks never run for a user who will be redirected.
									isHourglassInternalStrict(user?.email) ? (
										<Titled title="Internal - Metrics">
											<MetricsView />
										</Titled>
									) : (
										<Navigate to="entries" replace />
									)
								}
							/>
							<Route path="sandbox" element={<Titled title="Internal - Sandbox"><SandboxView /></Titled>} />
							<Route path="updates" element={<Titled title="Internal - Updates"><UpdatesView /></Titled>} />
							<Route path="*" element={<Navigate to="entries" replace />} />
						</Routes>
					</ErrorBoundary>
				</ColumnPickerProvider>
			</SidebarInset>
		</SidebarProvider>
	);
}
